import { ActionTree, MutationTree } from 'vuex'
import { Tag, TagCategory, UITag, UITopic, WordIndices } from '../../types/tag'
import {
  mapAnalysisTagsToCategories,
  mapTagsToCategories,
  TopicCategoryColors
} from '../../utils'
import { TopicTag } from '../../transcription.api'
import { tagsTypes } from './tagsPicker'
import { v4 as uuidv4 } from 'uuid'
import { Word } from '../../models/word'

export interface WordTags {
  [index: number]: UITag[]
}

export const NONE_SUB_CATEGORY = 'None'
export const PARTIAL_SUB_CATEGORY = 'Partial'

class State {
  categories: TagCategory[] = []
  analysisCategories: TagCategory[] = []
  topicTags: UITag[] = []
  deletedTags: UITag[] = []
  palette: TopicCategoryColors = {}
  analysisPalette: TopicCategoryColors = {}
  analysisTags: UITag[] = []
  wordsTagDictionary: WordTags = {}
  visibleWordsTagsDictionary: WordTags = {}
  wordsAnalysisTagDictionary: WordTags = {}
  topicsShown = true
  analysisShown = false
  tagsToAdd: UITag[] = []
  tagsToEdit: UITag[] = []
}

function createWordsDictionary(words: Word[]): WordTags {
  const obj: WordTags = {}
  words.forEach((word: Word) => {
    obj[word.index] = []
  })

  return obj
}

function areTagsIntersected(tag1: UITag, tag2: UITag): boolean {
  const startsInside =
    tag1.startWordIndex >= tag2.startWordIndex &&
    tag1.startWordIndex <= tag2.endWordIndex
  const endsInside =
    tag1.endWordIndex >= tag2.startWordIndex &&
    tag1.endWordIndex <= tag2.endWordIndex
  const fitsInside =
    tag1.startWordIndex >= tag2.startWordIndex &&
    tag1.endWordIndex <= tag2.endWordIndex
  const wrapsOutside =
    tag1.startWordIndex <= tag2.startWordIndex &&
    tag1.endWordIndex >= tag2.endWordIndex
  const wordsNearConnectionOnEnd = tag2.endWordIndex + 1 === tag1.startWordIndex
  const wordsNearConnectionOnStart =
    tag2.startWordIndex - 1 === tag1.endWordIndex

  return (
    startsInside ||
    endsInside ||
    fitsInside ||
    wrapsOutside ||
    wordsNearConnectionOnEnd ||
    wordsNearConnectionOnStart
  )
}

const getters = {
  categories(state: State): TagCategory[] {
    state.categories = mapTagsToCategories(
      state.topicTags.filter(t => !t.deleted),
      state.palette,
      state.categories
    )
    return state.categories
  },
  wordsTags(state: State) {
    return state.topicsShown
      ? state.visibleWordsTagsDictionary
      : state.wordsAnalysisTagDictionary
  },
  analysisCategories(state: State) {
    return state.analysisCategories
  },
  notDeletedTags(state: State): UITag[] {
    return state.topicTags.filter(t => !t.deleted)
  },
  visibleTags(state: State, getters: any): UITag[] {
    return (getters.notDeletedTags as UITag[]).filter(t => t.isVisible)
  },
  appliedTags(state: State): WordTags {
    return state.wordsTagDictionary
  },
  topicTags(state: State): UITag[] {
    return state.topicTags
  },
  deletedTags(state: State): UITag[] {
    return state.deletedTags
  },
  tagsToAdd(state: State): UITag[] {
    return state.tagsToAdd
  },
  editedTags(state: State): UITag[] {
    return state.tagsToEdit
  }
}

const mutations = {
  TOGGLE_ALL_TAGS(state: State, status: boolean) {
    state.categories.forEach((cat: TagCategory) => {
      cat.isOpen = status
      cat.tags.forEach((tag: UITag) => {
        tag.isVisible = status
      })
    })

    state.topicTags.forEach(tag => (tag.isVisible = status))

    const fillTagsFrom = Math.min(
      ...state.topicTags.filter(t => !t.deleted).map(t => t.startWordIndex)
    )
    const fillTagsTo = Math.max(
      ...state.topicTags.filter(t => !t.deleted).map(t => t.endWordIndex)
    )

    function showAllTags() {
      for (let i = fillTagsFrom; i <= fillTagsTo; i++) {
        state.visibleWordsTagsDictionary[i] = []
        state.visibleWordsTagsDictionary[i] = [...state.wordsTagDictionary[i]]
      }
    }

    function hideAllTags() {
      for (let i = fillTagsFrom; i <= fillTagsTo; i++) {
        state.visibleWordsTagsDictionary[i] = []
      }
    }

    if (status) {
      showAllTags()
    } else {
      hideAllTags()
    }
  },
  _TOGGLE_TAG(state: State, { id, status }) {
    const tagToToggle: UITag | undefined = state.topicTags.find(
      topic => topic.id === id
    )
    const tagInTagsList: UITag | undefined = state.categories
      .flatMap((c: TagCategory) => c.tags)
      .find((t: UITag) => t.id === id)

    if (!tagInTagsList || !tagToToggle) {
      return
    }

    function hideTag(tag: UITag) {
      const endIndex = Math.min(
        tag.endWordIndex,
        Object.keys(state.visibleWordsTagsDictionary).length - 1
      )
      for (let i = tag.startWordIndex; i <= endIndex; i++) {
        state.visibleWordsTagsDictionary[i] = state.visibleWordsTagsDictionary[
          i
        ].filter(t => t.id !== tag.id)
      }

      tag.isVisible = false
    }

    function showTag(tag: UITag) {
      const endIndex = Math.min(
        tag.endWordIndex,
        Object.keys(state.visibleWordsTagsDictionary).length
      )
      for (let i = tag.startWordIndex; i <= endIndex; i++) {
        if (tag.isVisible) {
          state.visibleWordsTagsDictionary[
            i
          ] = state.visibleWordsTagsDictionary[i].filter(t => t.id !== tag.id)
        } else {
          state.visibleWordsTagsDictionary[i].push(tag)
        }
      }

      tag.isVisible = !tag.isVisible
    }

    const tagsWithSimilarTopicId = state.topicTags.filter(
      t =>
        t.type === tagToToggle.type &&
        t.category === tagToToggle.category &&
        t.subCategory === tagToToggle.subCategory &&
        !t.deleted
    )

    const newStatus = status

    if (tagsWithSimilarTopicId.length > 1) {
      if (newStatus) {
        tagsWithSimilarTopicId.forEach(hideTag)
        tagsWithSimilarTopicId.forEach(showTag)
      } else {
        tagsWithSimilarTopicId.forEach(hideTag)
      }

      tagInTagsList.isVisible = newStatus
    } else {
      if (newStatus) {
        hideTag(tagToToggle)
        showTag(tagToToggle)
      } else {
        hideTag(tagToToggle)
      }
    }
  },
  TOGGLE_TAG(state: State, id: number) {
    const tagToToggle: UITag | undefined = state.topicTags.find(
      topic => topic.id === id
    )
    const tagInTagsList: UITag | undefined = state.categories
      .flatMap((c: TagCategory) => c.tags)
      .find((t: UITag) => t.id === id)

    if (!tagInTagsList || !tagToToggle) {
      return
    }

    function showTag(tag: UITag) {
      for (let i = tag.startWordIndex; i <= tag.endWordIndex; i++) {
        // Check whether we've gone past the end of the transcript-
        // Tag indices are not updated when transcripts are changed.
        if (
          !state.visibleWordsTagsDictionary ||
          !state.visibleWordsTagsDictionary[i]
        )
          continue

        if (tag.isVisible) {
          state.visibleWordsTagsDictionary[
            i
          ] = state.visibleWordsTagsDictionary[i].filter(t => t.id !== tag.id)
        } else {
          state.visibleWordsTagsDictionary[i].push(tag)
        }
      }

      tag.isVisible = !tag.isVisible
    }

    const tagsWithSimilarTopicId = state.topicTags.filter(
      t =>
        t.type === tagToToggle.type &&
        t.category === tagToToggle.category &&
        t.subCategory === tagToToggle.subCategory &&
        !t.deleted
    )

    if (tagsWithSimilarTopicId.length > 1) {
      tagsWithSimilarTopicId.forEach(showTag)
      tagInTagsList.isVisible = !tagInTagsList.isVisible
    } else {
      showTag(tagToToggle)
    }
  },
  REMOVE_TAG(state: State, { tag, index }: { tag: UITag; index?: number }) {
    const toUpdate = state.topicTags.find(t => t.id === tag.id)
    if (!toUpdate) {
      return
    }

    toUpdate.deleted = true
    state.topicTags = state.topicTags.filter(t => t.id !== toUpdate.id)

    for (let i = tag.startWordIndex; i <= tag.endWordIndex; i++) {
      state.wordsTagDictionary[i] = state.wordsTagDictionary[i].filter(
        t => t.id !== tag.id
      )
      state.visibleWordsTagsDictionary[i] = state.visibleWordsTagsDictionary[
        i
      ].filter(t => t.id !== tag.id)
    }
  },
  ADD_TAG_TO_TEXT(
    state: State,
    data: {
      id: number
      startIndex: number
      endIndex: number
      topics: UITopic[]
      speakerName: string
    }
  ) {
    state.tagsToAdd = []
    state.tagsToEdit = []
    state.deletedTags = []

    const newTags: UITag[] = data.topics.map((topic: UITopic, index) => ({
      id: index === 0 ? data.id || uuidv4() : uuidv4(),
      apiId: data.id,
      color: state.palette[topic.category.toLowerCase() || 'ungrouped'],
      title: topic.sub_category,
      createdAt: new Date().toUTCString(),
      subCategory: topic.sub_category,
      isVisible: true,
      topicId: topic.type + topic.category + topic.sub_category,
      type: topic.type,
      category: topic.category || 'Ungrouped',
      startWordIndex: data.startIndex,
      endWordIndex: data.endIndex,
      transcriptId: 0,
      transcriptVersion: 0,
      startWordIndexPart: 0,
      endWordIndexPart: 0,
      speaker: data.speakerName || '',
      flagged: null,
      extended: false,
      deleted: false,
      isNew: true
    }))

    let startWordIndex = data.startIndex
    let endWordIndex = data.endIndex

    // If tags with same topic ID are not visible - make them visible
    const tagsWithSameTopicIdToMakeVisible: UITag[] = []
    const tagsToAdd: UITag[] = []

    newTags.forEach((newTag: UITag) => {
      // const tagsWithSimilarTopicId = state.topicTags.filter(
      //     (tag: UITag) => tag.topicId === newTag.topicId && !tag.deleted
      // )

      const tagsWithSimilarTopicId =
        newTag.category === 'DNA'
          ? state.topicTags.filter(
              (tag: UITag) =>
                tag.type === newTag.type &&
                tag.category === newTag.category &&
                tag.subCategory === newTag.subCategory &&
                !tag.deleted
            )
          : []

      if (tagsWithSimilarTopicId.length) {
        // make tags with same topicId visible again
        const tagInTagsList = state.categories
          .flatMap((c: TagCategory) => c.tags)
          .find(
            (t: UITag) =>
              t.type === newTag.type &&
              t.category === newTag.category &&
              t.subCategory === newTag.subCategory
          )
        if (!tagInTagsList) {
          return
        }

        tagInTagsList.isVisible = !tagInTagsList.isVisible
      }

      const intersectedTags = tagsWithSimilarTopicId.filter(tag =>
        areTagsIntersected(newTag, tag)
      )

      // If a new tag intersects some tags with same topic id - join tags
      if (intersectedTags.length) {
        // Get new indices by getting the min and max of intersected tags
        startWordIndex = Math.min(
          ...intersectedTags.map(t => t.startWordIndex),
          newTag.startWordIndex
        )
        endWordIndex = Math.max(
          ...intersectedTags.map(t => t.endWordIndex),
          newTag.endWordIndex
        )

        // New tag gets the new start and end indices (joining tags)
        newTag.startWordIndex = startWordIndex
        newTag.endWordIndex = endWordIndex
        newTag.isVisible = intersectedTags[0].isVisible

        // newTag.isNew = false

        // Remove intersected tags from array of all tags
        const intersectedIds = intersectedTags.map(t => t.id)
        intersectedTags.forEach((tag: UITag) => (tag.deleted = true))
        state.topicTags = state.topicTags.filter(
          _t => !intersectedIds.includes(_t.id)
        )
        state.deletedTags = intersectedTags

        // Push joined tag into the array of tags

        if (!newTag.isVisible) {
          tagsWithSimilarTopicId.forEach((tag: UITag) => {
            tag.isVisible = true
          })
          newTag.isVisible = true
          tagsWithSameTopicIdToMakeVisible.push(
            ...tagsWithSimilarTopicId.filter(
              t => !intersectedIds.includes(t.id)
            )
          )
        }

        // Fill the words dictionary
        for (let i = startWordIndex; i <= endWordIndex; i++) {
          // Remove intersected tags
          state.wordsTagDictionary[i] = state.wordsTagDictionary[i].filter(
            t => !intersectedIds.includes(t.id)
          )
          state.visibleWordsTagsDictionary[
            i
          ] = state.visibleWordsTagsDictionary[i].filter(
            t => !intersectedIds.includes(t.id)
          )
        }
      } else if (tagsWithSimilarTopicId.length) {
        newTag.isVisible = tagsWithSimilarTopicId[0].isVisible

        if (!newTag.isVisible) {
          tagsWithSimilarTopicId.forEach((tag: UITag) => {
            tag.isVisible = true
          })
          newTag.isVisible = true
          tagsWithSameTopicIdToMakeVisible.push(...tagsWithSimilarTopicId)
        }
      }

      tagsToAdd.push(newTag)
    })

    state.tagsToAdd = [...tagsToAdd]
    state.topicTags.push(...tagsToAdd)

    const tagsToDisplay: UITag[] = [
      ...tagsToAdd,
      ...tagsWithSameTopicIdToMakeVisible
    ]
    tagsToDisplay.forEach((tag: UITag) => {
      for (let i = tag.startWordIndex; i <= tag.endWordIndex; i++) {
        state.wordsTagDictionary[i].push(tag)
        state.visibleWordsTagsDictionary[i].push(tag)
      }
    })
  },
  INIT_TOPIC_TAGS(
    state: State,
    data: {
      tags: TopicTag[]
      words: Word[]
      topicsPalette: TopicCategoryColors
      analysisPalette: TopicCategoryColors
    }
  ) {
    state.palette = {
      ...data.topicsPalette
    }

    state.analysisPalette = data.analysisPalette

    const analysisTags = data.tags.filter(
      tag => tag.type === tagsTypes.ANALYSIS
    )
    const topicTags = data.tags.filter(tag => tag.type === tagsTypes.TOPIC)

    state.wordsTagDictionary = createWordsDictionary(data.words)
    state.visibleWordsTagsDictionary = createWordsDictionary(data.words)
    state.wordsAnalysisTagDictionary = createWordsDictionary(data.words)

    state.topicTags = topicTags.map((t: TopicTag) => ({
      ...t,
      apiId: t.id,
      isVisible: true,
      title: t.subCategory,
      isNew: false,
      color: state.palette[t.category.toLowerCase()]
    }))
    state.analysisTags = analysisTags.map((t: TopicTag) => ({
      ...t,
      apiId: t.id,
      color: state.analysisPalette[t.category.toLowerCase()] || 'gray',
      isVisible: true,
      title: t.subCategory
    }))

    state.analysisCategories = mapAnalysisTagsToCategories(
      state.analysisTags,
      state.analysisPalette,
      state.analysisCategories
    )
    state.categories = mapTagsToCategories(
      state.topicTags,
      state.palette,
      state.categories
    )

    let editTagsFrom = Math.min(...topicTags.map(t => t.startWordIndex))
    let editTagsTo = Math.max(...topicTags.map(t => t.endWordIndex))

    for (let i = editTagsFrom; i <= editTagsTo; i++) {
      const tagsOnIndex: UITag[] = state.topicTags.filter(
        (tag: UITag) => i >= tag.startWordIndex && i <= tag.endWordIndex
      )
      state.visibleWordsTagsDictionary[i]?.push(...tagsOnIndex)
      state.wordsTagDictionary[i]?.push(...tagsOnIndex)
    }

    editTagsFrom = Math.min(...analysisTags.map(t => t.startWordIndex))
    editTagsTo = Math.max(...analysisTags.map(t => t.endWordIndex))

    for (let i = editTagsFrom; i <= editTagsTo; i++) {
      const tagsOnIndex: UITag[] = state.analysisTags.filter(
        (tag: UITag) => i >= tag.startWordIndex && i <= tag.endWordIndex
      )
      state.wordsAnalysisTagDictionary?.[i]?.push(...tagsOnIndex)
    }

    state.categories.forEach(c => (c.isOpen = true))
  },
  SHOW_ANALYSIS(state: State, length: number) {
    state.analysisShown = true
    state.topicsShown = false

    for (let i = 0; i < length; i++) {
      const tagsOnIndex: UITag[] = state.analysisTags.filter(
        (tag: UITag) =>
          i >= tag.startWordIndex && i <= tag.endWordIndex && tag.isVisible
      )
      state.wordsAnalysisTagDictionary[i] = []
      state.wordsAnalysisTagDictionary[i].push(...tagsOnIndex)
    }
  },
  SHOW_TOPICS(state: State, length: number) {
    state.topicsShown = true
    state.analysisShown = false
    for (let i = 0; i < length; i++) {
      const tagsOnIndex: UITag[] = state.topicTags.filter(
        (tag: UITag) =>
          i >= tag.startWordIndex && i <= tag.endWordIndex && tag.isVisible
      )
      state.wordsTagDictionary[i] = []
      state.wordsTagDictionary[i].push(...tagsOnIndex)
    }
  },
  TOGGLE_ANALYSIS_TAG(state: State, id: number) {
    const update: UITag | undefined = state.analysisTags.find(
      topic => topic.id === id
    )
    if (!update) {
      return
    }

    // find tags with same topic id
    const toUpdate = state.analysisTags.filter(
      t =>
        t.type === update.type &&
        t.category === update.category &&
        t.subCategory === update.subCategory
    )
    const toUpdateInUI = state.analysisCategories
      .flatMap(c => c.tags)
      .find(
        t =>
          t.type === update.type &&
          t.category === update.category &&
          t.subCategory === update.subCategory
      )

    if (!toUpdate || !toUpdateInUI) {
      return
    }

    toUpdate.forEach((tagToUpdate: UITag) => {
      if (tagToUpdate === toUpdateInUI) {
        tagToUpdate.isVisible = !tagToUpdate.isVisible
      } else {
        tagToUpdate.isVisible = !tagToUpdate.isVisible
        toUpdateInUI.isVisible = tagToUpdate.isVisible
      }

      if (tagToUpdate.wordIndices) {
        tagToUpdate.wordIndices.forEach((idx: WordIndices) => {
          for (let i = idx.startWordIndex; i <= idx.endWordIndex; i++) {
            if (tagToUpdate.isVisible) {
              state.wordsAnalysisTagDictionary?.[i]?.push({
                ...tagToUpdate,
                ...idx,
                wordIndices: undefined
              })
            } else {
              state.wordsAnalysisTagDictionary[
                i
              ] = state.wordsAnalysisTagDictionary?.[i]?.filter(
                t => t.id !== tagToUpdate.id
              )
            }
          }
        })
      } else {
        for (
          let i = tagToUpdate.startWordIndex;
          i <= tagToUpdate.endWordIndex;
          i++
        ) {
          if (tagToUpdate.isVisible) {
            state.wordsAnalysisTagDictionary?.[i]?.push(tagToUpdate)
          } else {
            state.wordsAnalysisTagDictionary[
              i
            ] = state.wordsAnalysisTagDictionary?.[i]?.filter(
              t => t.id !== tagToUpdate.id
            )
          }
        }
      }
    })
  },
  TOGGLE_CATEGORY_VISIBILITY(state: State, id: number) {
    const category: TagCategory | undefined = state.categories.find(
      c => c.id === id
    )

    if (!category) {
      return
    }

    category.isOpen = !category.isOpen
  },
  REINITIALIZE_TAGS(state: State, words: Word[]) {
    state.wordsTagDictionary = createWordsDictionary(words)
    state.visibleWordsTagsDictionary = createWordsDictionary(words)
    state.wordsAnalysisTagDictionary = createWordsDictionary(words)

    if (state.topicsShown) {
      const editTagsFrom = Math.min(
        ...state.topicTags.filter(t => !t.deleted).map(t => t.startWordIndex)
      )
      const editTagsTo = Math.max(
        ...state.topicTags.filter(t => !t.deleted).map(t => t.endWordIndex)
      )

      for (let i = editTagsFrom; i <= editTagsTo; i++) {
        const tagsOnIndex: UITag[] = state.topicTags.filter(
          (tag: UITag) =>
            i >= tag.startWordIndex && i <= tag.endWordIndex && !tag.deleted
        )
        state.visibleWordsTagsDictionary[i].push(
          ...tagsOnIndex.filter(t => t.isVisible)
        )
        state.wordsTagDictionary[i].push(...tagsOnIndex)
      }

      // editTagsFrom = Math.min(...analysisTags.map(t => t.startWordIndex))
      // editTagsTo = Math.max(...analysisTags.map(t => t.endWordIndex))
      //
      // for (let i = editTagsFrom; i <= editTagsTo; i++) {
      //     const tagsOnIndex: UITag[] = state.analysisTags.filter(
      //         (tag: UITag) => i >= tag.startWordIndex && i <= tag.endWordIndex
      //     )
      //     state.wordsAnalysisTagDictionary[i].push(...tagsOnIndex)
      // }
      //
      // state.categories.forEach(c => (c.isOpen = true))
    }
  },
  UPDATE_TAGS(state: State, data: { prevTags: UITag[]; newTags: UITag[] }) {
    data.prevTags.forEach((tag: UITag, index: number) => {
      const newTag = data.newTags[index]
      const tagToUpdate = state.topicTags.find(t => t.id === tag.id)
      if (!tagToUpdate) {
        return
      }

      tagToUpdate.id = newTag.id
    })
  }
} as MutationTree<State>

const actions = {
  toggleTag(context, id: number) {
    context.commit('TOGGLE_TAG', id)
  },
  toggleCategory(context, cat: { id: number; status: boolean }) {
    const category = context.state.categories.find(
      (c: TagCategory) => c.id === cat.id
    )
    if (!category) {
      return
    }

    category.tags.forEach((tag: UITag) => {
      context.commit('_TOGGLE_TAG', { id: tag.id, status: cat.status })
    })
  },
  toggleAllTags(context, status: boolean) {
    context.commit('TOGGLE_ALL_TAGS', status)
  },
  removeTag(context, data: { tag: Tag; index: number }) {
    context.commit('REMOVE_TAG', data)
  },
  addTagToText(
    context,
    data: {
      startIndex: number
      endIndex: number
      tags: TagCategory[]
      speakerName: string
    }
  ) {
    context.commit('ADD_TAG_TO_TEXT', data)
    localStorage.setItem(
      'transcript',
      JSON.stringify(context.rootGetters['transcriptTags/categories'])
    )
    return [context.getters['tagsToAdd'], context.getters['deletedTags']]
  },
  editTag(
    context,
    data: {
      wordIndex: number
      tag: UITag
      topics: UITopic
      speakerName: string
    }
  ) {
    context.commit('REMOVE_TAG', { tag: data.tag })
    context.commit('ADD_TAG_TO_TEXT', {
      id: data.tag.id,
      startIndex: data.tag.startWordIndex,
      endIndex: data.tag.endWordIndex,
      topics: data.topics,
      speakerName: data.speakerName
    })
    return [context.getters['tagsToAdd'], context.getters['deletedTags']]
  },
  initTopicTags(
    context,
    data: { tags: TopicTag[]; words: Word[]; palette: TopicCategoryColors }
  ) {
    context.commit('INIT_TOPIC_TAGS', data)
  },
  showAnalysis(context) {
    const length =
      context.rootGetters['conversation/transcript']?.words.length || 0
    context.commit('SHOW_ANALYSIS', length)
  },
  showTopics(context) {
    const length =
      context.rootGetters['conversation/transcript']?.words.length || 0
    context.commit('SHOW_TOPICS', length)
  },
  toggleAnalysisTag(context, id) {
    context.commit('TOGGLE_ANALYSIS_TAG', id)
  },
  toggleCategoryVisibility(context, id) {
    context.commit('TOGGLE_CATEGORY_VISIBILITY', id)
  },
  reinitializeTags(context, words: Word[]) {
    context.commit('REINITIALIZE_TAGS', words)
  },
  updateTags(context, data: { prevTags: UITag[]; newTags: UITag[] }) {
    context.commit('UPDATE_TAGS', data)
  }
} as ActionTree<State, any>

export default {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions
}
